/**
 * WhatsApp template builder — Meta WhatsApp Manager-style multi-step wizard.
 * Steps: 1) Category & name  2) Content (header incl. media samples uploaded to
 * S3, body + variables, footer, buttons)  3) Review & submit for Meta approval.
 *
 * Media header samples (image / video / document) are uploaded via the CCMS
 * FileService (S3) using useFileUpload; the returned URL is passed to 360dialog as
 * the template header_handle example.
 */

import { useState } from 'react';
import { IconTrash, IconPlus, IconPhoto, IconVideo, IconFile, IconCheck } from '@tabler/icons-react';
import { FileButton } from '@mantine/core';
import { CompactWizard } from '../../_shared/CompactWizard';
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  Textarea,
  Select,
  Button,
  ActionIcon,
  Divider,
  Alert,
  Badge,
  Image,
} from '../../../../../components/ui';
import { useFileUpload } from '../../../../../components/ccms-components/file-management/hooks';
import { TEMPLATE_CATEGORIES } from '../dialog360.types';
import type {
  CreateTemplateApiRequest,
  TemplateDefinitionComponent,
  TemplateButtonDefinition,
} from '../dialog360.types';

type HeaderType = 'NONE' | 'TEXT' | 'MEDIA' | 'LOCATION';
type MediaFormat = 'IMAGE' | 'VIDEO' | 'DOCUMENT';
interface ButtonDraft {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
  text: string;
  url: string;
  phoneNumber: string;
}

const MEDIA_ACCEPT: Record<MediaFormat, string> = {
  IMAGE: 'image/*',
  VIDEO: 'video/*',
  DOCUMENT: '.pdf,application/pdf',
};

function countVars(text: string): number {
  let max = 0;
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) max = Math.max(max, parseInt(m[1], 10));
  return max;
}

export function Dialog360TemplateBuilder({
  opened,
  onClose,
  onSubmit,
  submitting,
}: {
  opened: boolean;
  onClose: () => void;
  onSubmit: (req: CreateTemplateApiRequest) => Promise<void> | void;
  submitting?: boolean;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('UTILITY');
  const [language, setLanguage] = useState('en');

  const [headerType, setHeaderType] = useState<HeaderType>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [mediaFormat, setMediaFormat] = useState<MediaFormat>('IMAGE');
  const [mediaUrl, setMediaUrl] = useState<string>('');
  const [mediaName, setMediaName] = useState<string>('');

  const [bodyText, setBodyText] = useState('');
  const [bodyExamples, setBodyExamples] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState<ButtonDraft[]>([]);

  const { upload, uploading } = useFileUpload({
    domain: 'Communications',
    recordId: 'whatsapp-templates',
    maxSize: 16 * 1024 * 1024,
    onComplete: (files) => {
      if (files[0]?.url) setMediaUrl(files[0].url);
    },
  });

  const bodyVars = countVars(bodyText);
  const exampleValues = bodyExamples.split(',').map((s) => s.trim()).filter(Boolean);

  const reset = () => {
    setName(''); setCategory('UTILITY'); setLanguage('en');
    setHeaderType('NONE'); setHeaderText(''); setMediaFormat('IMAGE'); setMediaUrl(''); setMediaName('');
    setBodyText(''); setBodyExamples(''); setFooterText(''); setButtons([]);
  };

  const addButton = () => setButtons((b) => [...b, { type: 'QUICK_REPLY', text: '', url: '', phoneNumber: '' }]);
  const updateButton = (i: number, patch: Partial<ButtonDraft>) =>
    setButtons((b) => b.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeButton = (i: number) => setButtons((b) => b.filter((_, idx) => idx !== i));

  const handlePick = async (file: File | null) => {
    if (!file) return;
    setMediaName(file.name);
    setMediaUrl('');
    await upload([file]);
  };

  const buildRequest = (): CreateTemplateApiRequest => {
    const components: TemplateDefinitionComponent[] = [];

    if (headerType === 'TEXT' && headerText.trim()) {
      const hVars = countVars(headerText);
      components.push({ type: 'HEADER', format: 'TEXT', text: headerText.trim(), example: hVars > 0 ? { headerText: ['Sample'] } : undefined });
    } else if (headerType === 'MEDIA' && mediaUrl) {
      components.push({ type: 'HEADER', format: mediaFormat, example: { headerHandle: [mediaUrl] } });
    } else if (headerType === 'LOCATION') {
      components.push({ type: 'HEADER', format: 'LOCATION' });
    }

    components.push({ type: 'BODY', text: bodyText.trim(), example: bodyVars > 0 ? { bodyText: [exampleValues] } : undefined });

    if (footerText.trim()) components.push({ type: 'FOOTER', text: footerText.trim() });

    const validButtons = buttons.filter((b) => b.text.trim());
    if (validButtons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: validButtons.map<TemplateButtonDefinition>((b) => ({
          type: b.type,
          text: b.text.trim(),
          url: b.type === 'URL' ? b.url.trim() : undefined,
          phoneNumber: b.type === 'PHONE_NUMBER' ? b.phoneNumber.trim() : undefined,
        })),
      });
    }

    return { name: name.trim(), category, language: language.trim(), components };
  };

  const nameValid = /^[a-z0-9_]+$/.test(name.trim());

  return (
    <Modal opened={opened} onClose={() => { reset(); onClose(); }} title="Create WhatsApp Template" size="xl">
      <CompactWizard
        completeLabel="Submit for approval"
        loading={submitting}
        onComplete={async () => { await onSubmit(buildRequest()); reset(); onClose(); }}
        steps={[
          {
            title: 'Category',
            description: 'Name, category and language.',
            validate: () => name.trim().length > 0 && nameValid && language.trim().length > 0,
            content: (
              <Stack gap="md">
                <Select
                  label="Category"
                  description="Meta requires the right category; mis-categorised templates get rejected."
                  data={TEMPLATE_CATEGORIES.map((c) => ({ value: c, label: c }))}
                  value={category}
                  onChange={(v) => setCategory(v || 'UTILITY')}
                  required
                />
                <TextInput
                  label="Template name"
                  placeholder="order_confirmation"
                  description="Lowercase letters, numbers and underscores only"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  error={name.length > 0 && !nameValid ? 'Lowercase / numbers / underscores only' : undefined}
                  required
                />
                <TextInput label="Language" placeholder="en / en_US" value={language} onChange={(e) => setLanguage(e.currentTarget.value)} required />
              </Stack>
            ),
          },
          {
            title: 'Content',
            description: 'Header, body, footer and buttons.',
            validate: () =>
              bodyText.trim().length > 0 &&
              (bodyVars === 0 || exampleValues.length >= bodyVars) &&
              (headerType !== 'MEDIA' || !!mediaUrl),
            content: (
              <Stack gap="sm">
                <Divider label="Header (optional)" />
                <Select
                  label="Header type"
                  data={[
                    { value: 'NONE', label: 'None' },
                    { value: 'TEXT', label: 'Text' },
                    { value: 'MEDIA', label: 'Media (image / video / document)' },
                    { value: 'LOCATION', label: 'Location' },
                  ]}
                  value={headerType}
                  onChange={(v) => setHeaderType((v as HeaderType) || 'NONE')}
                />
                {headerType === 'TEXT' && (
                  <TextInput label="Header text" placeholder="Welcome!" value={headerText} onChange={(e) => setHeaderText(e.currentTarget.value)} />
                )}
                {headerType === 'MEDIA' && (
                  <Stack gap="xs">
                    <Group grow>
                      <Select
                        label="Media format"
                        data={[
                          { value: 'IMAGE', label: 'Image' },
                          { value: 'VIDEO', label: 'Video' },
                          { value: 'DOCUMENT', label: 'Document (PDF)' },
                        ]}
                        value={mediaFormat}
                        onChange={(v) => { setMediaFormat((v as MediaFormat) || 'IMAGE'); setMediaUrl(''); setMediaName(''); }}
                      />
                      <div>
                        <Text size="sm" fw={500} mb={4}>Sample media (uploaded to S3)</Text>
                        <FileButton onChange={handlePick} accept={MEDIA_ACCEPT[mediaFormat]}>
                          {(props) => (
                            <Button {...props} variant="light" loading={uploading}
                              leftSection={mediaFormat === 'IMAGE' ? <IconPhoto size={16} /> : mediaFormat === 'VIDEO' ? <IconVideo size={16} /> : <IconFile size={16} />}>
                              Choose {mediaFormat.toLowerCase()}
                            </Button>
                          )}
                        </FileButton>
                      </div>
                    </Group>
                    {mediaName && (
                      <Group gap="xs">
                        {mediaUrl ? <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>Uploaded</Badge> : <Badge color="gray" variant="light">Uploading…</Badge>}
                        <Text size="xs" c="dimmed">{mediaName}</Text>
                      </Group>
                    )}
                    {mediaUrl && mediaFormat === 'IMAGE' && <Image src={mediaUrl} h={120} w="auto" fit="contain" radius="sm" />}
                    <Text size="xs" c="dimmed">Note: WhatsApp template headers support image, video and document (not audio). Audio is available when sending media messages.</Text>
                  </Stack>
                )}

                <Divider label="Body (required)" />
                <Textarea label="Body text" description="Use {{1}}, {{2}}, … for variables" placeholder="Hi {{1}}, your order {{2}} is confirmed." minRows={5} autosize value={bodyText} onChange={(e) => setBodyText(e.currentTarget.value)} required />
                {bodyVars > 0 && (
                  <TextInput label={`Sample values for the ${bodyVars} variable(s)`} description="Comma-separated, in order" placeholder="John, 12345" value={bodyExamples} onChange={(e) => setBodyExamples(e.currentTarget.value)} />
                )}

                <Divider label="Footer (optional)" />
                <TextInput label="Footer text" placeholder="Reply STOP to opt out" value={footerText} onChange={(e) => setFooterText(e.currentTarget.value)} />

                <Divider label="Buttons (optional, max 3)" />
                <Stack gap="xs">
                  {buttons.map((b, i) => (
                    <Group key={i} align="flex-end" wrap="nowrap" gap="xs">
                      <Select w={150} label="Type" data={[{ value: 'QUICK_REPLY', label: 'Quick reply' }, { value: 'URL', label: 'URL' }, { value: 'PHONE_NUMBER', label: 'Phone' }]} value={b.type} onChange={(v) => updateButton(i, { type: (v as ButtonDraft['type']) || 'QUICK_REPLY' })} />
                      <TextInput label="Text" value={b.text} onChange={(e) => updateButton(i, { text: e.currentTarget.value })} style={{ flex: 1 }} />
                      {b.type === 'URL' && <TextInput label="URL" value={b.url} onChange={(e) => updateButton(i, { url: e.currentTarget.value })} style={{ flex: 1 }} />}
                      {b.type === 'PHONE_NUMBER' && <TextInput label="Phone" value={b.phoneNumber} onChange={(e) => updateButton(i, { phoneNumber: e.currentTarget.value })} style={{ flex: 1 }} />}
                      <ActionIcon color="red" variant="subtle" onClick={() => removeButton(i)} title="Remove"><IconTrash size={16} /></ActionIcon>
                    </Group>
                  ))}
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addButton} disabled={buttons.length >= 3}>Add button</Button>
                </Stack>
              </Stack>
            ),
          },
          {
            title: 'Review',
            description: 'Confirm and submit for Meta approval.',
            content: (
              <Stack gap="xs">
                {headerType === 'MEDIA' && !mediaUrl && <Alert color="yellow">Upload a sample media file for the header before submitting.</Alert>}
                <Group justify="space-between"><Text size="sm" c="dimmed">Name</Text><Text size="sm" ff="monospace">{name || '—'}</Text></Group>
                <Group justify="space-between"><Text size="sm" c="dimmed">Category</Text><Badge variant="light">{category}</Badge></Group>
                <Group justify="space-between"><Text size="sm" c="dimmed">Language</Text><Text size="sm">{language}</Text></Group>
                <Group justify="space-between"><Text size="sm" c="dimmed">Header</Text><Text size="sm">{headerType === 'MEDIA' ? `${mediaFormat} (sample uploaded)` : headerType}</Text></Group>
                <Divider my={4} />
                <Text size="xs" c="dimmed">Body</Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{bodyText || '—'}</Text>
              </Stack>
            ),
          },
        ]}
      />
    </Modal>
  );
}
